import React from 'react';
import { SlideNav } from './SlideNav';
import { SnakkerMark } from './SnakkerMark';
type Tone = 'paper' | 'accent';
type SlideFrameProps = {
  /** Running wayfinding label, e.g. "Step 02 — Perception". */
  marker?: string;
  /** Right-hand footer label. */
  meta?: string;
  tone?: Tone;
  /** Closing slide — swaps Next for Replay. */
  isLast?: boolean;
  children: React.ReactNode;
};
export function SlideFrame({
  marker,
  meta = 'Prepared for Roche · Established brand optimization',
  tone = 'paper',
  isLast = false,
  children
}: SlideFrameProps) {
  const accent = tone === 'accent';
  return <section className={['flex h-full w-full flex-col overflow-hidden px-[6%] pt-[3.4%] pb-[2.6%]', accent ? 'bg-gapAccent text-white' : 'bg-paper text-ink'].join(' ')} data-id="element-51" data-mp="29urcm4d9ygjv">
      <header className="flex shrink-0 items-center justify-between gap-[4%]" data-id="element-52" data-mp="yhgidf78ahng">
        <span className={['truncate text-[clamp(9px,1vw,16px)] font-semibold uppercase tracking-[0.16em]', accent ? 'text-white/75' : 'text-gapAccent'].join(' ')} data-id="element-53" data-mp="tgsswxyne8vc">
          {marker}
        </span>
        <SnakkerMark tone={tone} className="shrink-0" data-id="element-54" data-mp="1jj2rtcxt4ks5" />
      </header>

      <div className="flex min-h-0 flex-1 flex-col justify-center" data-id="element-55" data-mp="14ls8xiiu4cme">
        {children}
      </div>

      <footer className={['mt-[1.8vh] flex shrink-0 items-center justify-between gap-[4%] border-t pt-[1.4vh]', accent ? 'border-white/25' : 'border-ink-line'].join(' ')} data-id="element-56" data-mp="19kd9xk5wv0bw">
        <p className={['truncate text-[clamp(8px,0.85vw,13px)] font-medium', accent ? 'text-white/70' : 'text-ink-mute'].join(' ')} data-id="element-57" data-mp="10mb1wknrf7f4">
          Snakker thoughts-material — for discussion, not a final
          recommendation. <span aria-hidden="true" data-id="element-58" data-mp="cwgsye6ka305">·</span> {meta}
        </p>
        <SlideNav tone={tone} isLast={isLast} data-id="element-59" data-mp="gwzj83st3108" />
      </footer>
    </section>;
}
type SlideTitleProps = {
  children: React.ReactNode;
  sub?: React.ReactNode;
  tone?: Tone;
  size?: 'hero' | 'section';
};
export function SlideTitle({
  children,
  sub,
  tone = 'paper',
  size = 'section'
}: SlideTitleProps) {
  const accent = tone === 'accent';
  return <div className="shrink-0" data-id="element-60" data-mp="29uea1vcpqvgw">
      <h2 className={['font-display leading-[1.02] tracking-[-0.005em]', size === 'hero' ? 'text-[clamp(38px,6vw,100px)]' : 'text-[clamp(26px,4vw,64px)]', accent ? 'text-white' : 'text-ink'].join(' ')} data-id="element-61" data-mp="24urzjto9rtbs">
        {children}
      </h2>
      {sub && <p className={['mt-[1.2vh] max-w-[62ch] text-[clamp(12px,1.35vw,22px)] leading-[1.45]', accent ? 'text-white/80' : 'text-ink-soft'].join(' ')} data-id="element-62" data-mp="2sjl063fu6pcq">
          {sub}
        </p>}
    </div>;
}